import { CloseOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import VersionPreviewModal from '@zeus/core/modals/VersionPreviewModal';
import ReadmeHistory from '_/common/components/readme/ReadmeHistory';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import { useProjectReadmeVersions, useUpdateProjectReadme, PROJECT_QUERY_KEY } from '_/hooks/useProjects';
import React, { useState, useEffect, useCallback } from 'react';

function ProjectReadmeHistory() {
  const history = useNavigate();
  const queryClient = useQueryClient();
  const { setBreadcrumbs, setActionButtons } = useTopHeader();
  const { selectedOrgSlug, selectedOrganization } = useOrganization();
  const { selectedProject: project, selectedProjectSlug } = useProject();

  // React Query hooks
  const { data: readmeVersionsData } = useProjectReadmeVersions(project?.id);
  const readmeVersions = readmeVersionsData?.items || [];

  const updateReadmeMutation = useUpdateProjectReadme(selectedProjectSlug ?? '', project?.id);

  // Local state
  const [selectedVersion, setSelectedVersion] = useState({});
  const [previewModalVisible, setPreviewModalVisible] = useState(false);

  const handleReturnToProject = useCallback(() => {
    void history({ to: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}` });
  }, [history, selectedOrgSlug, selectedProjectSlug]);

  // Set up header with breadcrumbs and action buttons
  useEffect(() => {
    setBreadcrumbs([
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${selectedOrgSlug}` },
      { title: 'Projects', href: `/orgs/${selectedOrgSlug}/projects` },
      {
        title: project?.name || '',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/home`,
      },
      { title: 'README History' },
    ]);
    setActionButtons([
      {
        onClick: handleReturnToProject,
        icon: <CloseOutlined />,
        label: 'Close',
      },
    ]);

    return () => {
      setBreadcrumbs([]);
      setActionButtons([]);
    };
  }, [
    setBreadcrumbs,
    setActionButtons,
    selectedOrganization,
    selectedOrgSlug,
    project,
    selectedProjectSlug,
    handleReturnToProject,
  ]);

  const restoreToPreviousVersion = async (version) => {
    const restoredReadme = version.after.content;

    await updateReadmeMutation.mutateAsync(restoredReadme, {
      onSuccess: () => {
        // Invalidate project query to refresh project data
        if (selectedProjectSlug) {
          void queryClient.invalidateQueries({ queryKey: PROJECT_QUERY_KEY(selectedProjectSlug) });
        }
        void history({ to: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}` });

        if (previewModalVisible) {
          setPreviewModalVisible(false);
          setSelectedVersion({});
        }
      },
    });
  };

  const previewPreviousVersion = (version) => {
    setSelectedVersion(version);
    setPreviewModalVisible(!previewModalVisible);
  };

  return (
    <div>
      <ReadmeHistory
        readmeVersions={readmeVersions}
        onRestoreClick={restoreToPreviousVersion}
        onPreviewClick={previewPreviousVersion}
      />

      <VersionPreviewModal
        open={previewModalVisible}
        versionData={selectedVersion}
        onRestoreClick={restoreToPreviousVersion}
        onCancelClick={previewPreviousVersion}
      />
    </div>
  );
}

export default ProjectReadmeHistory;
